/**
 * useContactPreferences - Hooks לניהול העדפות איש קשר
 * מספק פונקציות CRUD סטנדרטיות + פונקציות מותאמות אישית
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ContactPreferences, ContactPreferenceType } from "../interfaces/DTO/contactPreferences";
import { contactPreferencesService } from "../services/contactPreferencesService";
import { createCachedEntityHooks } from "./createEntityHooks";

// יצירת hooks בסיסיים באמצעות ה-factory
export const {
  useAll: useAllContactPreferences,
  useEntityById: useContactPreferencesById,
  useCreateEntity: useCreateContactPreferences,
  useUpdateEntity: useUpdateContactPreferences,
  usePartialUpdateEntity: usePartialUpdateContactPreferences,
  useDeleteEntity: useDeleteContactPreferences,
} = createCachedEntityHooks<ContactPreferences>({
  entityName: "contactPreferences",
  service: contactPreferencesService,
  enableCacheById: true,
  staleTimeMs: 5 * 60 * 1000, // 5 דקות
  fieldIndexes: {
    contact_id: () => (contactId) => contactPreferencesService.getByContactId(contactId as number),
  },
});

/**
 * שליפת כל ההעדפות של איש קשר מסוים
 */
export function useContactPreferencesByContactId(contactId: number) {
  return useQuery({
    queryKey: ["contactPreferences", "byContact", contactId],
    queryFn: () => contactPreferencesService.getByContactId(contactId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * שליפת העדפות לפי איש קשר וסוג העדפה
 */
export function useContactPreferencesByContactAndType(
  contactId: number,
  preferenceType: ContactPreferenceType
) {
  return useQuery({
    queryKey: ["contactPreferences", "byContactAndType", contactId, preferenceType],
    queryFn: () => contactPreferencesService.getByContactAndType(contactId, preferenceType),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * שליפת העדפה ספציפית לפי איש קשר, סוג ותקופה
 */
export function useContactPreferencesByContactTypeAndPeriod(
  contactId: number,
  preferenceType: ContactPreferenceType,
  reportedPeriod: string
) {
  return useQuery({
    queryKey: ["contactPreferences", "byContactTypeAndPeriod", contactId, preferenceType, reportedPeriod],
    queryFn: () => contactPreferencesService.getByContactTypeAndPeriod(contactId, preferenceType, reportedPeriod),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * שליפת העדפות משפחתיות לפי family_id
 */
export function useFamilyPreferencesByFamilyId(familyId: number) {
  return useQuery({
    queryKey: ["contactPreferences", "family", familyId],
    queryFn: () => contactPreferencesService.getFamilyPreferences(familyId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * שליפת ההעדפות האחרונות (לפי reported_period) לכל preference_type
 * מחזיר רשומה אחת לכל סוג העדפה עם התקופה המדווחת האחרונה
 */
export function useContactPreferencesLastByContactId(contactId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["contactPreferences", "last", contactId],
    queryFn: () => contactPreferencesService.getByContactLast(contactId),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled
  });
}

/**
 * שליפת ההעדפות המשפחתיות האחרונות (לפי reported_period) לכל preference_type
 * מחזיר רק העדפות משפחתיות עם התקופה המדווחת האחרונה
 */
export function useFamilyPreferencesLastByContactId(familyId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["contactPreferences", "familyLast", familyId],
    queryFn: () => contactPreferencesService.getFamilyPreferencesLast(familyId),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled
  });
}

/**
 * Mutation ליצירת מספר העדפות בבת אחת
 */
export function useBulkCreateContactPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: Omit<ContactPreferences, 'id'>[]) =>
      contactPreferencesService.bulkCreate(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contactPreferences"] });
    },
  });
}

/**
 * Mutation לעדכון מספר העדפות בבת אחת
 */
export function useBulkPartialUpdateContactPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: Partial<ContactPreferences & { id: number }>[]) =>
      contactPreferencesService.bulkPartialUpdate(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contactPreferences"] });
    },
  });
}
